import React, { useEffect, useLayoutEffect, useRef } from "react";
// @ts-ignore
import { Previewer, registerHandlers } from "pagedjs";
import { T } from "services/lang/LanguageService";
import { isProd, queryParam } from "utils/utils";
import { useGetDashboard } from "api/api-hooks/dashboards-hooks";
import { Dashboard } from "api/model/dashboard/Dashboard";
import getReportPagedJsStyle from "./report-style";
import DashboardReport from "./DashboardReportWrapper";
import PagedJsReportHandler from "./PagedJsReportHandler";

const PROD_GENERATION_TIMEOUT = 40000;
const DEV_GENERATION_TIMEOUT = 5000;
const GEN_DELAY = 1000;

const REPORT_EVT = "dashboard-report-status";

declare global {
  interface Window {
    loadedPreview: boolean;
    reportGenStart: Date;
  }

  interface Element {
    style: any;
  }
}

const DashboardReportViewPaged = ({
  refTime,
  containerId,
  dashboardOverride,
}: {
  refTime?: string;
  containerId?: string;
  dashboardOverride?: Dashboard;
}) => {
  const dashboardId = parseInt(queryParam("dashboardId")!!, 10);
  const referenceTime = refTime ?? queryParam("referenceTime");
  const isBot = queryParam("robot") === "true";

  const query = useGetDashboard(dashboardId, {
    enabled: !dashboardOverride,
  });

  const dashboard = dashboardOverride ?? query.data;
  const reportOptions = dashboard?.reportOptions;
  const loadingTimoutRef = useRef<NodeJS.Timeout | null>(null);

  const isValid = () => {
    if (!referenceTime) return false;

    // Check if date valid
    const date = new Date(referenceTime);
    return !Number.isNaN(date.getTime());
  };

  const dispatchReportStatus = (status: "finished" | "error") => {
    window.dispatchEvent(new CustomEvent(REPORT_EVT, { detail: status }));
  };

  const userAlert = (message: string) => {
    if (!isBot) alert(message);
  };

  const reportComponentsStatusCallback = (componentsStatus: "finished" | "error") => {
    console.log("Dashboard components loading status", componentsStatus);

    if (loadingTimoutRef.current) clearTimeout(loadingTimoutRef.current);

    if (componentsStatus === "error") {
      dispatchReportStatus("error");
      userAlert(T("alert.error-generating-report-some-component-failed"));
    }

    if (componentsStatus === "finished") {
      setTimeout(() => {
        injectDynamicStyles();
        renderPagePreview();
      }, GEN_DELAY);
    }
  };

  const injectDynamicStyles = () => {
    const style = document.createElement("style");

    style.innerHTML = `
      .pagedjs_page {
        background: ${reportOptions?.pageLayout.bgColor ?? "white"} !important;
        margin-bottom: 2rem;
        border-radius: 5px;
      }
  
      .pagedjs_margin-top {
        border-bottom: ${reportOptions?.marginBoxes?.topCenter?.hideBorder ? "none" : "1px solid gray"};
      }
  
      .pagedjs_margin-bottom {
        border-top: ${reportOptions?.marginBoxes?.bottomCenter?.hideBorder ? "none" : "1px solid gray"};
      }
    `;
    document.head.appendChild(style);
  };

  const getOrCreatePreviewContainer = () => {
    let el = document.querySelector(`#${containerId}`);
    if (!el && containerId) {
      el = document.createElement("div");
      el.id = containerId;
      document.body.appendChild(el);
    }
    return el;
  };

  const injectWrapperStylesIntoIframe = () => {
    const iframe = document.querySelector("iframe.pagedjs_iframe");
    if (!iframe) {
      console.log("Paged.js iframe not found.");
      return;
    }

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) return;

    const styles = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"));
    styles.forEach(node => {
      if (node.tagName === "STYLE") {
        const styleClone = iframeDoc.createElement("style");
        styleClone.innerHTML = node.innerHTML;
        iframeDoc.head.appendChild(styleClone);
      } else if (node.tagName === "LINK") {
        const linkClone = iframeDoc.createElement("link");
        linkClone.rel = "stylesheet";
        linkClone.href = (node as HTMLLinkElement).href;
        iframeDoc.head.appendChild(linkClone);
      }
    });

    console.log("Injected wrapper styles into Paged.js iframe.");
  };

  const renderPagePreview = () => {
    window.loadedPreview = true;
    window.reportGenStart = new Date();

    console.log(`Render start time: ${window.reportGenStart.toISOString()}`);

    const pagedPreviewEl = getOrCreatePreviewContainer();

    const isContainerMode = Boolean(containerId);
    const rootSelector = isContainerMode ? "#dashboard-report-wrapper-paged" : "#root";

    const rootEl = document.querySelector(rootSelector);

    if (!rootEl) {
      console.error(`❌ Root element not found (expected ${rootSelector})`);
      return;
    }

    registerHandlers(PagedJsReportHandler);

    const cssUrls = [`${window.jsBasePath}/assets/reports/core.css`, getReportPagedJsStyle(reportOptions!)];

    const previewer = new Previewer();
    previewer
      .preview(rootEl.innerHTML, cssUrls, pagedPreviewEl)
      .then((flow: any) => {
        const wrapper = document.querySelector("#dashboard-report-wrapper-paged");
        if (wrapper) wrapper.style.display = "none";

        if (isContainerMode) {
          injectWrapperStylesIntoIframe();
        }

        const reportGenEnd = new Date();
        const elapsedSeconds = Math.round((reportGenEnd.getTime() - window.reportGenStart.getTime()) / 1000);

        console.log(`Render end time ${reportGenEnd.toISOString()}`);

        if (isContainerMode) {
          console.log(`Rendered total pages in ${elapsedSeconds}s`);
        } else {
          console.log(`Rendered total ${flow?.total ?? "?"} pages. Took ${elapsedSeconds}s.`);
        }

        dispatchReportStatus("finished");
      })
      .catch((err: Error) => {
        console.error("❌ Paged.js preview generation failed", err);
        dispatchReportStatus("error");
      });
  };

  useEffect(() => {
    console.log("Robot?", isBot);
  }, []);

  const getTimeoutValue = () => {
    if (reportOptions?.meta?.generationTimeout) {
      return reportOptions.meta.generationTimeout * 1000;
    }
    return isProd() ? PROD_GENERATION_TIMEOUT : DEV_GENERATION_TIMEOUT;
  };

  useLayoutEffect(() => {
    if (!isValid()) return;
    if (!dashboard) return;
    if (window.loadedPreview) return;
    loadingTimoutRef.current = setTimeout(() => {
      dispatchReportStatus("error");
      userAlert(
        T("alert.report-generation-reached-timeout-some-components-taking-longer-than-expected-to-load")
      );
    }, getTimeoutValue());

    if (loadingTimoutRef.current) clearTimeout(loadingTimoutRef.current);
  });

  return (
    <>
      {!isValid() && <div>{T("error.invalid-report-options-missing-or-invalid-referencetime")}</div>}

      {isValid() && !dashboard && T("word.loading")}

      {isValid() && dashboard && (
        <DashboardReport
          dashboard={dashboard}
          referenceTime={referenceTime!!}
          finishCallback={reportComponentsStatusCallback}
        />
      )}
    </>
  );
};

export default DashboardReportViewPaged;
